import { cn } from "@gymflow/helpers";
import { SubscriptionSessionPackDTO } from "@gymflow/types";
import { useTranslation } from "react-i18next";

import { TabsType } from "../../UserMember/UserSessions/constants";
import { HelpCircleIcon, InfinityIcon } from "..";
import CustomTooltip from "../CustomTooltip";
import { Coins03Icon } from "../icons/Coins03Icon";
import { CreditPackActions } from "./components/CreditPackActions";
import { CreditPackExpiryInfo } from "./components/CreditPackExpiryInfo";
import { CreditPackItemStatus } from "./components/CreditPackItemStatus";

export type CreditPackItemType = SubscriptionSessionPackDTO;

type CreditPackItemProps = {
  as: "member" | "staff";
  creditPack: CreditPackItemType;
  selectedItemId?: number;
  setSelectedItemId: (item: number) => void;
  activeTabId: TabsType;
  userMemberId?: string;
};

export const CreditPackItem = ({
  as,
  selectedItemId,
  setSelectedItemId,
  activeTabId,
  userMemberId = "",
  creditPack,
}: CreditPackItemProps) => {
  const {
    id,
    name,
    sessionsLeft,
    termsConditions,
    sessionsUnlimited,
    partOfMembership,
    membershipName,
  } = creditPack;
  const { t } = useTranslation();

  const hideActions = as === "member" || !userMemberId;

  return (
    <div
      className={cn(
        "relative min-w-[19rem] flex cursor-pointer flex-row justify-between rounded-2xl border border-gray-200 bg-gray-0 dark:border-gray-600 dark:bg-gray-950 px-4 pb-2 pt-3",
        {
          "ring-secondary-500 ring-2 ring-offset-0": selectedItemId === id,
        },
      )}
      onClick={() => setSelectedItemId(id)}
    >
      {partOfMembership && (
        <div
          className={cn(
            "flex absolute -top-2 right-4 gap-1 justify-center items-center py-0.5 pr-1 pl-2 bg-gray-50 dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-600",
            {
              "ring-secondary-500 ring-2 ring-offset-0": selectedItemId === id,
              "px-2": hideActions,
            },
          )}
        >
          <div className="dark:text-gray-25 text-xs font-medium text-gray-950">
            {t("components.creditPackItem.partOfMembership")}
          </div>
          {!hideActions && (
            <CustomTooltip
              message={t("components.creditPackItem.limitedControls")}
              hideIfEmpty
            >
              <HelpCircleIcon
                className="size-4"
                pathClassName="stroke-gray-500 dark:stroke-gray-400"
              />
            </CustomTooltip>
          )}
        </div>
      )}

      <div className="flex flex-col justify-center overflow-x-hidden">
        <CustomTooltip message={name} asChild>
          <div className="dark:text-gray-25 truncate text-base font-bold text-gray-950">
            {name}
          </div>
        </CustomTooltip>
        <div className="flex flex-col items-start gap-1.5 lg:flex-row lg:items-center">
          <CreditPackItemStatus creditPack={creditPack} />
          <CreditPackExpiryInfo creditPack={creditPack} />
        </div>
        <div className="flex items-center gap-1.5 py-1">
          <a
            className="text-secondary-500 hover:text-secondary-700 active:text-secondary-900 min-w-fit text-xs font-semibold"
            href={termsConditions}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("components.creditPackItem.viewTerms")}
          </a>
          {partOfMembership && membershipName && (
            <div className="flex items-center gap-1.5 overflow-x-hidden">
              <div className="size-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-gray-700" />
              <CustomTooltip message={membershipName} asChild>
                <div className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
                  {membershipName}
                </div>
              </CustomTooltip>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-row items-center justify-center gap-3">
        <div className="flex flex-row items-center justify-center gap-1">
          <div className="dark:text-gray-25 text-xl font-bold text-gray-950">
            {sessionsUnlimited ? (
              <InfinityIcon className="dark:stroke-gray-25 stroke-gray-950" />
            ) : (
              sessionsLeft
            )}
          </div>
          <Coins03Icon
            className="size-6"
            pathClassName="stroke-secondary-500"
          />
        </div>
        <CreditPackActions
          creditPack={creditPack}
          activeTabId={activeTabId}
          hideActions={hideActions}
          id={id}
          userMemberId={userMemberId}
        />
      </div>
    </div>
  );
};
